import { ArrowLeftIcon, Button, ButtonLink, CreditCardIcon, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicePreview } from '../../enrollments/components/widgets';
import { hasBillingAmount, RosterRow, toBillableRows, WizardRoom } from '../AddFamilies/types';
import { buildFamilyPreview } from './invoicePreview';

interface ReviewBillingProps {
  stepper?: ReactNode;
  roster: RosterRow[];
  frequency: RosterRow['billingFrequency'];
  // The org's real rooms, used to label each student's room.
  rooms: WizardRoom[];
  isSaving: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

const dollars = (v: string) => Number(v.replace(/[^0-9.]/g, '')) || 0;

/**
 * Final step of Add-to-billing. No parent-invite email here (these families are
 * already in the account); this is a plain "here's what we're about to charge,
 * confirm" summary before the recurring plans are created.
 */
export function ReviewBilling(p: ReviewBillingProps) {
  const { t } = useTranslation();
  // Label a student's room from the org's real rooms.
  const roomLabel = (id: string) => p.rooms.find((r) => r.id === id)?.name ?? '—';
  // `billingAmount` is the amount we bill this family (tuition or copay per pay type).
  const rowAmount = (r: RosterRow) => dollars(r.billingAmount);
  const { billed, total } = useMemo(() => {
    const withRate = p.roster.filter((r) => hasBillingAmount(r));
    return { billed: withRate, total: withRate.reduce((s, r) => s + rowAmount(r), 0) };
  }, [p.roster]);

  // The first invoice's due date, derived through the SAME helper `onConfirm`
  // uses, so what this step previews and what gets written can't disagree.
  // Reading `r.startDate` directly showed `blankRow()`'s today default, because
  // the shared `StepBilling` collects only the recurring `dueDate`.
  const startDate = useMemo(() => toBillableRows(billed)[0]?.startDate || '', [billed]);
  const billFor = p.roster.find((r) => r.billFor)?.billFor || 'next';
  const skipped = p.roster.length - billed.length;
  const frequencyLabel =
    p.frequency === 'weekly'
      ? t('addToBilling.review.frequency.weekly')
      : p.frequency === 'biweekly'
        ? t('addToBilling.review.frequency.biweekly')
        : t('addToBilling.review.frequency.monthly');
  const startDateLabel = startDate
    ? dayjs(startDate).format('MMMM D, YYYY')
    : t('addToBilling.review.eachStudentStartDate');
  const billedNoun = billed.length === 1 ? t('addToBilling.review.studentNoun') : t('addToBilling.review.studentsNoun');

  // Expand a family to preview its first 3 invoices (prorated first).
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const toggle = (id: string) =>
    setExpandedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  // Proration heads-up: a start date partway through the billing period means the
  // first invoice covers only the partial period. (Monthly starting on the 1st is
  // a clean period start, so no proration.)
  const periodWord =
    p.frequency === 'weekly'
      ? t('addToBilling.review.period.week')
      : p.frequency === 'biweekly'
        ? t('addToBilling.review.period.billingPeriod')
        : t('addToBilling.review.period.month');
  const startsMidPeriod = !!startDate && (p.frequency !== 'monthly' || dayjs(startDate).date() !== 1);
  const showProration = billed.length > 0 && startsMidPeriod;

  return (
    <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6" data-testid="atb-step-review">
      {p.stepper && <div className="border-b border-gray-200 pb-4">{p.stepper}</div>}

      <div>
        <h2 className="flex items-center gap-2 text-2xl font-bold text-navy-950">
          <CreditCardIcon className="h-6 w-6 text-navy-950" /> {t('addToBilling.review.title')}
        </h2>
        <p className="mt-1 text-sm text-gray-600">{t('addToBilling.review.description')}</p>
      </div>

      <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-navy-950">
        {t('addToBilling.review.summary', {
          date: startDateLabel,
          total: `$${total.toLocaleString()}`,
          frequency: frequencyLabel,
          count: billed.length,
          noun: billedNoun,
        })}
        {showProration && (
          <div className="mt-1.5 text-navy-950/80">
            {t('addToBilling.review.prorationNotice', { period: periodWord })}
          </div>
        )}
      </div>

      <div>
        <div className="text-muted grid grid-cols-[2fr_1fr_1fr_auto] gap-3 px-4 pb-1 text-xs font-medium">
          <span>{t('addToBilling.review.studentColumn')}</span>
          <span>{t('addToBilling.review.roomColumn')}</span>
          <span className="text-right">{t('addToBilling.review.amountColumn', { frequency: frequencyLabel })}</span>
          <span className="w-20" />
        </div>
        <div className="space-y-2">
          {billed.map((r) => {
            const name = `${r.childFirstName} ${r.childLastName}`.trim();
            const open = expandedIds.includes(r.id);
            return (
              <div key={r.id} className="overflow-hidden rounded-md border border-gray-200">
                <button
                  type="button"
                  onClick={() => toggle(r.id)}
                  className="grid w-full grid-cols-[2fr_1fr_1fr_auto] items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50"
                >
                  <span className="text-sm font-medium text-gray-900">{name}</span>
                  <span className="text-muted text-sm">{roomLabel(r.room)}</span>
                  <span className="text-right text-sm text-gray-900">${rowAmount(r).toLocaleString()}</span>
                  <span className="w-20 text-right text-xs font-medium text-blue-900">
                    {open ? t('addToBilling.review.hideAction') : t('addToBilling.review.previewAction')}{' '}
                    {open ? '▾' : '▸'}
                  </span>
                </button>
                {open && (
                  <InvoicePreview
                    header={t('addToBilling.review.previewDescription', {
                      name,
                      date: startDate ? dayjs(startDate).format('MMMM D, YYYY') : '—',
                    })}
                    nextInvoices={buildFamilyPreview(r, startDate, billFor)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {skipped > 0 && (
        <p className="text-sm text-gray-500">{t('addToBilling.review.skippedStudents', { count: skipped })}</p>
      )}

      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <ButtonLink
          content={t('addToBilling.review.backAction')}
          iconDirection="left"
          icon={<ArrowLeftIcon className="size-4" />}
          onClick={p.onBack}
        />
        <Button
          primary
          size={WidgetSizeEnum.MEDIUM}
          disabled={billed.length === 0 || p.isSaving}
          loading={p.isSaving}
          onClick={p.onConfirm}
        >
          {p.isSaving
            ? t('addToBilling.review.savingLabel')
            : t('addToBilling.review.confirmAction', { count: billed.length })}
        </Button>
      </div>
    </section>
  );
}
